/**
 * RepairRuntimeDialog — Feature A gate dialog (runtime-reinstate-and-fleet-
 * state-plan.md §A.3.5). Single-runtime "Repair": destroy + recreate the
 * container from a fresh spec against the SAME persistent volume — data-safe.
 *
 * Operator-mandated: a distinct amber/orange caution accent (set apart from
 * the neutral Connect/Upgrade and the red destructive delete), and a dialog
 * that THOROUGHLY explains the operation before confirm is enabled — per-tier
 * consequence, the data-preserved rationale, and the docker no-rollback caveat.
 *
 * Tiers (increasing blast radius), confirm phrase = `<TIER> <containerName>`:
 *   Restart  — reboot the container in place (a blip).
 *   Recreate — rebuild a fresh container (default). Keeps ALL data.
 *   Reset    — recreate + re-author the env-derived config. Keeps user data.
 *
 * The parent runs the two-phase plan→execute API; this dialog yields
 * (tier, reason) once the operator types the exact phrase.
 */

import { useEffect, useState } from "react";
import { Eraser, RotateCcw, Wrench } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export type RepairTier = "restart" | "recreate" | "reset";

export interface RepairRuntimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The container being repaired — drives the title + confirm phrase. */
  containerName?: string;
  deployTarget?: string;
  /** Invoked with the chosen tier + reason once the operator confirms. */
  onConfirm: (tier: RepairTier, reason: string) => void;
}

const TIERS: Array<{
  value: RepairTier;
  icon: typeof Wrench;
  title: string;
  body: string;
}> = [
  {
    value: "restart",
    icon: RotateCcw,
    title: "Restart",
    body: "Reboot the container in place — a brief blip. Try this first for a transient hang.",
  },
  {
    value: "recreate",
    icon: Wrench,
    title: "Recreate",
    body: "Rebuild a fresh container over the same volume. Fixes a wedged / crashed / won’t-start runtime. Keeps ALL data.",
  },
  {
    value: "reset",
    icon: Eraser,
    title: "Reset",
    body: "Recreate AND re-author the env-derived config (openclaw.json). Deep repair for a corrupt config/credential layer. Keeps user data.",
  },
];

export function RepairRuntimeDialog({
  open,
  onOpenChange,
  containerName,
  deployTarget,
  onConfirm,
}: RepairRuntimeDialogProps) {
  const [tier, setTier] = useState<RepairTier>("recreate");
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open) {
      setTier("recreate");
      setReason("");
      setConfirmText("");
    }
  }, [open]);

  const name = containerName ?? "";
  const confirmPhrase = `${tier.toUpperCase()} ${name}`;
  const phraseOk = confirmText.trim() === confirmPhrase;
  const reasonOk = reason.trim().length >= 3;
  const ready = phraseOk && reasonOk;

  const tierConsequence: Record<RepairTier, string> = {
    restart: "Reboot in place — a brief blip.",
    recreate: "A short downtime while the container re-boots.",
    reset: "A short downtime; the env-derived config is rewritten.",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg space-y-4" data-testid="runtime-repair-dialog">
        <DialogHeader className="items-center sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl text-foreground">
            <Wrench className="h-6 w-6 text-amber-500" />
            Repair runtime
          </DialogTitle>
          <DialogDescription>
            Repairs the runtime{" "}
            <strong className="font-mono text-foreground">{name}</strong>
            {deployTarget ? <> ({deployTarget})</> : null}.
          </DialogDescription>
        </DialogHeader>

        {/* Tier selector */}
        <div className="grid grid-cols-3 gap-2">
          {TIERS.map((t) => {
            const selected = tier === t.value;
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTier(t.value)}
                aria-pressed={selected}
                data-testid={`runtime-repair-tier-${t.value}`}
                className={
                  "rounded-lg border p-3 text-left transition-colors " +
                  (selected
                    ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300 dark:border-amber-600 dark:bg-amber-900/30 dark:ring-amber-700"
                    : "border-border bg-panel-2 hover:bg-accent")
                }
              >
                <div
                  className={
                    "flex items-center gap-1.5 text-sm font-semibold " +
                    (selected ? "text-amber-700 dark:text-amber-300" : "text-foreground")
                  }
                >
                  <Icon className="h-4 w-4 text-amber-500" />
                  {t.title}
                </div>
                <p className="mt-1 text-[11px] leading-snug text-muted">{t.body}</p>
              </button>
            );
          })}
        </div>

        {/* Data-safety rationale */}
        <div
          className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
          data-testid="runtime-repair-data-safe"
        >
          <p className="font-semibold">Your data is safe.</p>
          <p className="mt-0.5">
            Cases, sessions, memory and credentials live on the detached volume
            (Azure Files / host bind-mount), <strong>never inside the container</strong> —
            so recreate is byte-safe. This is <strong>not</strong> a delete, a wipe,
            or a permanent teardown.
          </p>
        </div>

        {/* Per-tier consequence + availability caveat */}
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <p>
            <strong>What to expect:</strong> {tierConsequence[tier]}
          </p>
          <p className="mt-1">
            On <strong>Azure</strong> the prior revision keeps serving until the new
            one is healthy. On <strong>docker</strong> a failed recreate leaves the
            runtime down (data still safe) — re-run to recover.
          </p>
        </div>

        {/* Reason (required) */}
        <div className="space-y-1">
          <label htmlFor="runtime-repair-reason" className="text-xs font-medium text-muted">
            Reason (required, audited):
          </label>
          <textarea
            id="runtime-repair-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. recover stale-bind-mount container after host reboot"
            data-testid="runtime-repair-reason-input"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-amber-400 focus:ring-1 focus:ring-amber-300/40"
          />
        </div>

        {/* Type-to-confirm */}
        <div className="space-y-1">
          <label htmlFor="runtime-repair-confirm" className="text-xs font-medium text-muted">
            Type{" "}
            <span className="font-mono font-semibold text-foreground">{confirmPhrase}</span>{" "}
            to enable Repair:
          </label>
          <input
            id="runtime-repair-confirm"
            type="text"
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={confirmPhrase}
            data-testid="runtime-repair-confirm-input"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 font-mono text-sm text-foreground outline-none placeholder:text-faint focus:border-amber-400 focus:ring-1 focus:ring-amber-300/40"
          />
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="runtime-repair-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready}
            onClick={() => ready && onConfirm(tier, reason.trim())}
            data-testid="runtime-repair-confirm"
            className="rounded-md bg-amber-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Repair ({tier})
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
